import React from 'react';
import { ResourceField, ResourceFormPage } from '../../components/resource/ResourceFormPage';
import { ExpedicaoProduto } from '../../types/estoque';
import {
  expedicoesService,
  pedidosExpedicaoService,
  disponibilidadeSaidaService,
} from '../../services';
import { makeOptionLoader } from '../../utils/optionLoaders';

type DisponibilidadeOption = {
  value: string;
  label: string;
  payload: {
    produto: number;
    lote?: string | number | null;
    armazem: number;
    localizacao?: string;
    quantidade_disponivel: number;
  };
};

const EstoqueField: React.FC<{
  value: unknown;
  onChange: (val: any) => void;
  options: DisponibilidadeOption[];
}> = ({ value, onChange, options }) => {
  const [selectedOption, setSelectedOption] = React.useState<DisponibilidadeOption | null>(null);

  React.useEffect(() => {
    if (value && typeof value === 'string') {
      const found = options.find((opt) => opt.value === value);
      if (found) setSelectedOption(found);
    }
  }, [value, options]);

  return (
    <div className="d-flex flex-column gap-2">
      <div>
        <label className="form-label">Produto / Lote / Armazém</label>
        <select
          className="form-select"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => {
            const selected = options.find((opt) => opt.value === e.target.value);
            if (selected) {
              setSelectedOption(selected);
              onChange(e.target.value);
            }
          }}
          required
        >
          <option value="">Selecione</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      {selectedOption && (
        <div>
          <label className="form-label">Estoque atual</label>
          <input
            type="text"
            className="form-control"
            value={selectedOption.payload.quantidade_disponivel.toLocaleString('pt-BR')}
            readOnly
          />
        </div>
      )}
    </div>
  );
};

export const ExpedicoesFormPage: React.FC = () => {
  const [options, setOptions] = React.useState<DisponibilidadeOption[]>([]);

  React.useEffect(() => {
    disponibilidadeSaidaService
      .list({ page_size: 1000 })
      .then((res) => {
        const opts: DisponibilidadeOption[] = [];
        (res.results || []).forEach((item) => {
          item.lotes.forEach((lote, idx) => {
            const value = `${item.produto_id}::${lote.armazem_id}::${lote.lote_id ?? ''}::${lote.localizacao ?? ''}::${idx}`;
            const label = [
              item.produto_codigo ?? item.produto_id,
              item.produto_descricao,
              lote.armazem_nome ?? `Armazem #${lote.armazem_id}`,
              lote.lote_codigo ? `Lote ${lote.lote_codigo}` : undefined,
              lote.localizacao ? `Loc ${lote.localizacao}` : undefined,
              `Disp. ${lote.quantidade_disponivel}`,
            ]
              .filter(Boolean)
              .join(' · ');

            opts.push({
              value,
              label,
              payload: {
                produto: item.produto_id,
                lote: lote.lote_id ?? null,
                armazem: lote.armazem_id,
                localizacao: lote.localizacao ?? '',
                quantidade_disponivel: lote.quantidade_disponivel,
              },
            });
          });
        });
        setOptions(opts);
      })
      .catch((err) => console.error('Erro ao carregar disponibilidade de saída', err));
  }, []);

  const fields: ResourceField<ExpedicaoProduto>[] = [
    {
      name: 'pedido',
      label: 'Pedido',
      type: 'async-select',
      valueType: 'number',
      loadOptions: makeOptionLoader(
        pedidosExpedicaoService.list,
        (item: any) => item.pedido_info ?? item.numero_pedido_cliente ?? String(item.id),
      ),
    },
    {
      name: 'estoque_produto',
      label: 'Estoque',
      render: ({ value, onChange }) => (
        <EstoqueField value={value} onChange={onChange} options={options} />
      ),
    },
    { name: 'data_saida', label: 'Data de saida', type: 'date' },
    { name: 'transportadora', label: 'Transportadora' },
    { name: 'placa_veiculo', label: 'Placa do veiculo' },
    { name: 'motorista', label: 'Motorista' },
    { name: 'quantidade_expedida', label: 'Quantidade expedida', type: 'number' },
    { name: 'observacoes', label: 'Observacoes', type: 'textarea' },
  ];

  return (
    <ResourceFormPage
      title="Nova expedicao"
      description="Registre a saida do pedido."
      fields={fields}
      service={expedicoesService}
      redirectPath="/admin/estoque/expedicoes"
      cancelPath="/admin/estoque/expedicoes"
    />
  );
};
